/**
 * lib/supabase.ts
 * ----------------
 * What: Centralised Supabase client configured for the app with persistent auth
 * session storage.
 * Why: Provides a single source of truth for API access and ensures consistent
 * session persistence across platforms (web/native) using a safe storage backend.
 * Notes: Also attaches a global auth state listener to aid debugging of token
 * refresh/sign-out issues during development.
 */
import { createClient } from '@supabase/supabase-js';
import env from '../src/config/env';
import { safeStorage } from '../utils/storage';

if (!env.supabaseUrl || !env.supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(env.supabaseUrl, env.supabaseAnonKey, {
  auth: {
    persistSession: true,
    storage: safeStorage,
  },
});

// 🔐 Add a global listener so we can debug unexpected log-outs or token refresh issues
supabase.auth.onAuthStateChange((event, session) => {
  if ((globalThis as any).__VERBOSE_LOGS__ !== false) {
    /* eslint-disable no-console */
    console.log('🔐 [supabase] Global auth state change:', {
      event,
      hasSession: !!session,
      userId: session?.user?.id,
      accessTokenExpiresIn: session?.expires_in,
      refreshToken: session?.refresh_token ? `len:${session.refresh_token.length}` : null,
    });
    /* eslint-enable no-console */
  }
});
